import React, { useEffect, useRef, useState } from 'react';
import { FieldLabel } from '@measured/puck';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CustomTooltip from '../../../components/common/tooltip';
import InputField from '../../../components/common/input-field';
import { IAddressOptions } from '../../interface/formInterface';
import { Switch, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { Select } from '../../../page-builder/components/form-fields/Select';
import { CountryWithStateList } from '../../data/country';
import { IDefaultOption } from '../../interface/customerInterface';
import MultiSelectSearch from '../../components/form-fields/MultiSelect';
import BackgroungColorComponent from '../../components/customComponents/BackgroungColorComponent';
import Border from './Border';
import ColorPicker from './ColorPicker';
import { placeholderfontWeightOptions } from './TextCustomInput';
import { placeHolderHeightOptions, textAlignOption } from './height';
import { fontFamilies } from './options';
import WithAccordion from '../../components/customComponents/CustomAccordion';
import QuilHelper from './quilHelper';
import PaddingComponent from '../../components/customComponents/PaddingComponent';
import NewMarginFiled from './newMarginField';
import Outline from './Outline';

interface addressFormFieldProps {
  value: IAddressOptions;
  onChange: (_value: IAddressOptions) => void;
  name: string;
  formName?: string;
  index?: number;
}

export const AddressFormField = ({
  value,
  onChange,
  name,
  formName,
  index,
}: addressFormFieldProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<IDefaultOption[]>([]);
  const [stateOptions, setStateOptions] = useState<IDefaultOption[]>([]);
  const isShowfieldInForm = Object?.prototype?.hasOwnProperty?.call(
    value,
    'show',
  );
  const positionRef = useRef<number>(0);
  const statePositionRef = useRef<number>(0);
  const setPostionRef = (value: number) => {
    positionRef.current = value;
  };
  const setStatePostionRef = (value: number) => {
    statePositionRef.current = value;
  };

  const separateWords = (input: string) => {
    if (!input) {
      return '';
    }
    return input.replace(/([a-z])([A-Z])/g, '$1 $2');
  };

  const selectAllOption = { label: 'Select All', value: 'Select All' };
  const handleTypeChange = (
    selectedValue: IDefaultOption[],
    allOptions: IDefaultOption[],
  ): IDefaultOption[] => {
    const isSelectAllSelected = selectedValue.some(
      item => item.value === selectAllOption.value,
    );

    const cleanedSelection = selectedValue.filter(
      item => item.value !== selectAllOption.value,
    );

    const allItems = allOptions.filter(
      item => item.value !== selectAllOption.value,
    );
    // Case 1: "Select All" selected → return all items (excluding "Select All")
    if (isSelectAllSelected) {
      return allItems;
    }
    // Case 2: Deselect individual item(s) after "Select All" → return remaining selected
    if (cleanedSelection.length < allItems.length) {
      return cleanedSelection;
    }
    // Default: return current selection
    return selectedValue;
  };

  useEffect(() => {
    const options =
      CountryWithStateList?.map(country => ({
        label: country.name,
        value: country.code,
      })) || [];

    setOptions([selectAllOption, ...options]);
    const filterdCountry = options.filter(country => {
      return value?.showOnlyCountry?.some(item => item.value === country.value);
    });
    const states: IDefaultOption[] = filterdCountry.flatMap(
      item =>
        CountryWithStateList?.find(
          country => country.code === item.value,
        )?.provinces?.map(province => ({
          label: province.name,
          value: province.id != null ? String(province.id) : '',
        })) || [],
    );

    setStateOptions([selectAllOption, ...states]);
  }, [
    formName,
    value?.showOnlyCountry,
    value?.showOnlyState,
    value?.defaultCountry,
  ]);

  return (
    <div>
      <div
        className="flex justify-between font-bold cursor-pointer py-2 bg-slate-100 rounded-md pagebuilder-theme"
        onClick={() => setIsOpen(!isOpen)}>
        <h3>{separateWords(name)}</h3>
        {!isOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
      </div>
      {isOpen && (
        <div className="flex flex-col gap-3 border border-gray-300 rounded p-2 w-full">
          {isShowfieldInForm && !value.disableRequiredValidation ? (
            <div className="flex justify-between items-center">
              <p className="text-sm font-semibold">Show field</p>
              <div className="flex gap-0.5 items-center">
                <Tooltip
                  title={`Toggle this switch to show ${name} field in the form`}
                  placement="top-start">
                  <InfoIcon fontSize="small" className="h-1 w-1" />
                </Tooltip>
                <Switch
                  onChange={e => {
                    onChange({ ...value, show: e.target.checked });
                  }}
                  checked={value?.show}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              </div>
            </div>
          ) : null}

          {value?.show || !isShowfieldInForm ? (
            <>
              {formName === 'Shipping Form' &&
              separateWords(name) == 'Address1' ? (
                <>
                  <div className="flex justify-between items-center">
                    <p className="text-sm font-semibold">Restrict P.O Boxes</p>
                    <div className="flex gap-0.5 items-center">
                      <Tooltip
                        title={`Toggle this switch to restrict P.O Boxes for ${name} field`}
                        placement="top-start">
                        <InfoIcon fontSize="small" className="h-1 w-1" />
                      </Tooltip>
                      <Switch
                        onChange={e => {
                          onChange({
                            ...value,
                            restrictPOBox: e.target.checked,
                          });
                        }}
                        checked={value?.restrictPOBox}
                        inputProps={{ 'aria-label': 'controlled' }}
                      />
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <p className="text-sm font-semibold">
                      Restrict Military Addresses
                    </p>
                    <div className="flex gap-0.5 items-center">
                      <Tooltip
                        title={`Toggle this switch to restrict Military Addresses for ${name} field`}
                        placement="top-start">
                        <InfoIcon fontSize="small" className="h-1 w-1" />
                      </Tooltip>
                      <Switch
                        onChange={e => {
                          onChange({
                            ...value,
                            restrictMilitaryAddress: e.target.checked,
                          });
                        }}
                        checked={value?.restrictMilitaryAddress}
                        inputProps={{ 'aria-label': 'controlled' }}
                      />
                    </div>
                  </div>
                </>
              ) : null}
              <WithAccordion title="Header">
                <QuilHelper
                  value={value.header?.text || ''}
                  onChange={val => {
                    onChange({
                      ...value,
                      header: {
                        ...value.header,
                        text: val,
                      },
                    });
                  }}
                  enableLink={true}
                  id={(index || 0) + 1}
                />
                <div className="flex justify-between items-center mb-3">
                  <label>Show Header</label>
                  <Switch
                    className="flex justify-between items-center mb-3"
                    checked={value?.header?.hide || false}
                    onChange={e => {
                      const selectedValue = e.target.checked;
                      onChange({
                        ...value,
                        header: {
                          ...value.header,
                          hide: selectedValue,
                        },
                      });
                    }}
                  />
                </div>

                <PaddingComponent
                  value={value?.header?.padding}
                  onChange={val => {
                    onChange({
                      ...value,
                      header: {
                        ...value.header,
                        padding: val,
                      },
                    });
                  }}
                  title="Padding"
                />
                <NewMarginFiled
                  value={value?.header?.margin}
                  onChange={val => {
                    onChange({
                      ...value,
                      header: {
                        ...value.header,
                        margin: val,
                      },
                    });
                  }}
                  title="Margin"
                />
              </WithAccordion>

              <FieldLabel
                label={
                  (
                    <CustomTooltip title="Text">
                      <span>Placeholder</span>
                    </CustomTooltip>
                  ) as React.ReactNode as string
                }
                el="div">
                <InputField
                  value={value?.placeholder}
                  onChange={text => onChange({ ...value, placeholder: text })}
                />
              </FieldLabel>

              {separateWords(name) == 'Country Code' && (
                <>
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip title="showOnlyCountry">
                          <span>Show Only Country</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div">
                    <MultiSelectSearch<IDefaultOption>
                      placeholder="Select Country"
                      position={positionRef.current}
                      className="!m-0 custom-multiselect"
                      setPostionRef={setPostionRef}
                      onInputChange={text => {
                        const filteredOptions = options.filter(option =>
                          option.label
                            .toLowerCase()
                            .includes(text.toLowerCase()),
                        );
                        setOptions(filteredOptions);
                        return filteredOptions;
                      }}
                      labelKey="label"
                      valueKey="value"
                      value={value?.showOnlyCountry || []}
                      onChange={event => {
                        const states: IDefaultOption[] = event.flatMap(
                          item =>
                            CountryWithStateList?.find(
                              country => country.code === item.value,
                            )?.provinces?.map(province => ({
                              label: province.name,
                              value:
                                province.id != null ? String(province.id) : '',
                            })) || [],
                        );
                        setStateOptions(states);

                        onChange({
                          ...value,
                          defaultCountry: '',
                          showOnlyState: !event.length
                            ? []
                            : value?.showOnlyState?.filter(item =>
                                states.some(
                                  state => state?.value === item?.value,
                                ),
                              ) || [],
                          showOnlyCountry: handleTypeChange(event, options),
                        });
                      }}
                      options={options}
                    />
                  </FieldLabel>
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip title="showOnlyStates">
                          <span>Show Only States/Provinces</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div">
                    <MultiSelectSearch<IDefaultOption>
                      placeholder="Select States/Provinces"
                      position={statePositionRef.current}
                      className="!m-0 custom-multiselect"
                      setPostionRef={setStatePostionRef}
                      onInputChange={text => {
                        const filteredOptions = stateOptions.filter(option =>
                          option.label
                            .toLowerCase()
                            .includes(text.toLowerCase()),
                        );
                        setStateOptions(filteredOptions);
                        return filteredOptions;
                      }}
                      labelKey="label"
                      valueKey="value"
                      value={value?.showOnlyState || []}
                      onChange={event =>
                        onChange({
                          ...value,
                          showOnlyState: handleTypeChange(event, stateOptions),
                        })
                      }
                      options={stateOptions}
                    />
                  </FieldLabel>
                  {formName && formName === 'Shipping Form' && (
                    <FieldLabel
                      label={
                        (
                          <CustomTooltip title="DefaultCountry">
                            <span>Default Country</span>
                          </CustomTooltip>
                        ) as React.ReactNode as string
                      }
                      el="div">
                      <Select
                        value={value?.defaultCountry}
                        onChange={event =>
                          onChange({
                            ...value,
                            defaultCountry: event.target.value,
                          })
                        }
                        options={value?.showOnlyCountry || []}
                      />
                    </FieldLabel>
                  )}
                </>
              )}
              {/* {separateWords(name) !== 'Country Code' && (
                <> */}
              {!['ProvinceCode', 'CountryCode'].includes(name) && (
                <ColorPicker
                  value={value?.color}
                  onChange={newColor => onChange({ ...value, color: newColor })}
                  label="Color"
                  tooltip="Choose a color for the checkbox"
                />
              )}

              <FieldLabel
                label={
                  (
                    <CustomTooltip title="Text">
                      <span>Font Size</span>
                    </CustomTooltip>
                  ) as React.ReactNode as string
                }
                el="div">
                <InputField
                  value={value?.fontSize}
                  onChange={e => {
                    let newValue = e.replace(/[^0-9%]/g, '');

                    // Ensure that only one '%' symbol remains at the end
                    if (newValue?.includes('%')) {
                      newValue =
                        newValue.replace(/%.*%/g, '%').replace(/%/g, '') + '%';
                    }

                    // If the string is empty or contains only whitespace, set to 'auto'
                    if (!newValue.trim()) {
                      newValue = 'auto';
                    }

                    // If the value includes both digits and '%', clean up any non-digit/% characters
                    if (/\d/.test(newValue) && /%/.test(newValue)) {
                      newValue = newValue.replace(/[^0-9%]/g, '');
                    }

                    // Default to 'auto' if the value is still empty
                    if (!newValue.trim()) {
                      newValue = 'auto';
                    }

                    onChange({ ...value, fontSize: newValue });
                  }}
                />
              </FieldLabel>
              <FieldLabel
                label={
                  (
                    <CustomTooltip title="fontWeight">
                      <span>Font Weight</span>
                    </CustomTooltip>
                  ) as React.ReactNode as string
                }
                el="div">
                <Select
                  value={value?.fontWeight}
                  onChange={event =>
                    onChange({
                      ...value,
                      fontWeight: event.target.value,
                    })
                  }
                  options={placeholderfontWeightOptions}
                />
              </FieldLabel>

              <FieldLabel
                label={
                  (
                    <CustomTooltip title="fontWeight">
                      <span>Line Height</span>
                    </CustomTooltip>
                  ) as React.ReactNode as string
                }
                el="div">
                <Select
                  value={value?.lineHeight}
                  onChange={event =>
                    onChange({
                      ...value,
                      lineHeight: event.target.value,
                    })
                  }
                  options={placeHolderHeightOptions}
                />
              </FieldLabel>
              <FieldLabel
                label={
                  (
                    <CustomTooltip title="Text">
                      <span>Letter Space</span>
                    </CustomTooltip>
                  ) as React.ReactNode as string
                }
                el="div">
                <InputField
                  value={value?.letterSpace}
                  onChange={e => {
                    const text = e.replace(/[^0-9%]/g, '');
                    onChange({ ...value, letterSpace: text });
                  }}
                />
              </FieldLabel>

              <FieldLabel
                label={
                  (
                    <CustomTooltip title="fontWeight">
                      <span>Text Align</span>
                    </CustomTooltip>
                  ) as React.ReactNode as string
                }
                el="div">
                <Select
                  value={value?.textAlign}
                  onChange={event =>
                    onChange({
                      ...value,
                      textAlign: event.target.value,
                    })
                  }
                  options={textAlignOption}
                />
              </FieldLabel>

              <FieldLabel
                label={
                  (
                    <CustomTooltip title="fontWeight">
                      <span>Font Family</span>
                    </CustomTooltip>
                  ) as React.ReactNode as string
                }
                el="div">
                <Select
                  value={value?.fontFamily}
                  onChange={event =>
                    onChange({
                      ...value,
                      fontFamily: event.target.value,
                    })
                  }
                  options={fontFamilies}
                />
              </FieldLabel>

              <Border
                value={value.border}
                onChange={newBorder => {
                  onChange({ ...value, border: newBorder });
                }}
                title="Border"
              />
              <Outline
                value={value.outline}
                onChange={newOutline => {
                  onChange({ ...value, outline: newOutline });
                }}
                title="Outline"
              />
              <BackgroungColorComponent
                value={value.textColor || { color: '', isInherit: false }}
                onChange={newColor => {
                  onChange({ ...value, textColor: newColor });
                }}
                title="Input Text Color"
                label="Input Text Color"
              />
              {/* </>
              )} */}
              <BackgroungColorComponent
                value={
                  value.textBackgroundColor || {
                    color: '',
                    isInherit: false,
                  }
                }
                onChange={newColor => {
                  onChange({ ...value, textBackgroundColor: newColor });
                }}
                title="Background Color"
              />

              {value?.disableRequiredValidation ? null : (
                <div className="flex justify-between items-center">
                  <p className="text-sm font-semibold">Required validation</p>
                  <div className="flex gap-0.5 items-center">
                    <Tooltip
                      title={`Toggle this switch to make ${name} field required`}
                      placement="top-start">
                      <InfoIcon fontSize="small" className="h-1 w-1" />
                    </Tooltip>
                    <Switch
                      onChange={e => {
                        onChange({
                          ...value,
                          requiredValidation: e.target.checked,
                        });
                      }}
                      checked={value?.requiredValidation}
                      inputProps={{ 'aria-label': 'controlled' }}
                    />
                  </div>
                </div>
              )}
              {value?.requiredValidation ? (
                <FieldLabel
                  label={
                    (
                      <CustomTooltip title="Text">
                        <span>Validation Text</span>
                      </CustomTooltip>
                    ) as React.ReactNode as string
                  }
                  el="div">
                  <InputField
                    value={value?.validationText}
                    onChange={text =>
                      onChange({ ...value, validationText: text })
                    }
                  />
                </FieldLabel>
              ) : null}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
};
